const { canSpeculate, prepareImage, displayImage } = await import(
  document.querySelector('script[data-tcs-media-cache]')?.dataset.tcsMediaCache ||
    './tcs-variant-image-cache.js'
);

/**
 * <tcs-product-card> — progressive enhancement for the TCS product card.
 * Spec (specimen sheet §3d): tapping a swatch swaps the card image in place,
 * moves the selected ring, and updates the hidden variant id so Quick Add
 * adds the selected shade. WC handles wishlist persistence and button state.
 *
 * Everything works without JS: swatches are decorative, forms submit the
 * default variant.
 *
 * SHOPIFY STANDARD EVENTS (R-007)
 * -------------------------------
 * The card dispatches `shopify:product:view` the way Horizon's own card does.
 * Horizon gets this by extending ProductComponent (assets/view-event-elements.js
 * -> createViewEventElement); we get it from the same factory, so there is one
 * mechanism on the storefront rather than a parallel TCS one.
 *
 * Three things have to line up, and all three are load-bearing:
 *   1. the `view-event-payload` attribute, written by snippets/tcs-product-card.liquid
 *      from Shopify's `standard_event_data` Liquid filter;
 *   2. this base class, which observes the element and dispatches on intersect;
 *   3. the `@shopify/events` import map entry in snippets/scripts.liquid, which
 *      Horizon points at Shopify's CDN.
 * The attribute alone does nothing. A card carrying the payload on a plain
 * HTMLElement is silent, which is exactly the state this file was in and why
 * the loss went unnoticed from 2026-08-30.
 *
 * `defaultTrigger: 'intersect'` matches Horizon's ProductComponent: a card in a
 * grid or a rail counts as viewed when it scrolls into view, not when it is
 * rendered into the DOM below the fold. The factory overrides this by itself
 * inside a <dialog> (quick add, cart drawer), so those surfaces need nothing.
 *
 * Loaded via dynamic import rather than a static one so that a failure to reach
 * Shopify's CDN costs us the analytics and NOT the card: an unresolvable static
 * import would take the whole module down with it, and with it the swatches and
 * the heart. Horizon's own card takes the harder dependency; ours does not need
 * to, and this component renders on every discovery surface we have.
 */

/** @type {typeof HTMLElement} */
let CardBase = HTMLElement;

try {
  const { createViewEventElement } = await import('@shopify/events');
  CardBase = createViewEventElement(HTMLElement, { defaultTrigger: 'intersect' });
} catch {
  // Analytics degrade, the card does not. Deliberately silent: this runs on
  // every card on the page and a console error per card would be noise.
}

class TcsProductCard extends CardBase {
  #images;
  #selection;

  #descriptor(swatch) {
    return {
      src: swatch.dataset.variantImage,
      width: Number(swatch.dataset.variantWidth) || 600,
      height: Number(swatch.dataset.variantHeight) || 600,
      alt: swatch.dataset.variantAlt || '',
    };
  }

  #prepare = (event) => {
    if (!canSpeculate()) return;
    const image = this.querySelector('.tcs-card__img');
    if (!image || !image.complete || !image.naturalWidth) return;
    // Engagement with this card prepares its existing (at most four) swatches.
    for (const swatch of this.querySelectorAll('[data-tcs-swatch][data-variant-image]'))
      prepareImage(this.#descriptor(swatch), image.getBoundingClientRect().width, {
        signal: this.#images.signal,
      }).catch(() => {});
  };
  connectedCallback() {
    // Arms the view-event observer when the base class provides one; a no-op
    // on the HTMLElement fallback.
    super.connectedCallback?.();
    this.addEventListener('click', this.#onClick);
    this.#images = new AbortController();
    for (const type of ['pointerover', 'focusin', 'pointerdown'])
      this.addEventListener(type, this.#prepare, { signal: this.#images.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#images?.abort();
    this.#selection?.abort();
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    const swatch = event.target.closest('[data-tcs-swatch]');
    if (swatch) {
      event.preventDefault();
      this.#selectSwatch(swatch);
      return;
    }
  };

  #selectSwatch(swatch) {
    for (const dot of this.querySelectorAll('[data-tcs-swatch]')) {
      dot.setAttribute('aria-pressed', String(dot === swatch));
    }

    const variantId = swatch.dataset.variantId;
    const hidden = this.querySelector('[data-tcs-variant-id]');
    if (variantId && hidden) hidden.value = variantId;
    const wishlist = this.querySelector('tcs-wishlist');
    if (variantId && wishlist) wishlist.dataset.variantId = variantId;
    const restock = this.querySelector('tcs-restock');
    if (swatch.dataset.variantAvailable !== undefined) {
      const available = swatch.dataset.variantAvailable === 'true';
      const price = this.querySelector('[data-tcs-card-price]');
      const compare = this.querySelector('[data-tcs-card-compare]');
      // The Liquid writes these as plain text (money | strip_html), so textContent
      // is both safe and exact; markup in a store money format is never parsed.
      if (price && swatch.dataset.variantPrice) price.textContent = swatch.dataset.variantPrice;
      if (compare) {
        compare.textContent = swatch.dataset.variantCompare || '';
        compare.hidden = !swatch.dataset.variantCompare;
      }

      const addAction = this.querySelector('[data-tcs-card-available]');
      const unavailableAction = this.querySelector('[data-tcs-card-unavailable]');
      if (addAction) addAction.hidden = !available;
      if (unavailableAction) unavailableAction.hidden = available;
      this.classList.toggle('tcs-card--soldout', !available);
      this.querySelector('.tcs-stars')?.classList.toggle('tcs-stars--muted', !available);
      const badge = this.querySelector('[data-tcs-card-badge]');
      if (badge) {
        badge.textContent = available
          ? badge.dataset.availableBadge || ''
          : badge.dataset.soldOutLabel || '';
        badge.hidden = available && !badge.dataset.availableBadge;
        badge.classList.toggle('tcs-card__badge--soldout', !available);
      }
      // Shade-only cards with a sold-out shade render their WC mount at page load
      // (tcs-buy-button.liquid), so WC has fetched this product before the press;
      // retargeting shows the TCS Notify me button in this same frame.
      if (!available && variantId)
        restock?.selectVariant?.(variantId, swatch.dataset.variantTitle || '');
      // An in-stock shade hides the sold-out action: drop a Notify me press
      // still waiting for WC, or it would open the dialog (or the product page)
      // for the shade the shopper just left.
      else if (available) restock?.cancel?.();
    } else if (variantId && restock) {
      restock.selectVariant?.(variantId, swatch.dataset.variantTitle || '');
    }

    const image = swatch.dataset.variantImage;
    const img = this.querySelector('.tcs-card__img');
    this.#selection?.abort();
    this.#selection = new AbortController();
    if (image && img) {
      const signal = this.#selection.signal;
      displayImage(this.#descriptor(swatch), img.getBoundingClientRect().width, {
        signal,
        sizes: img.sizes,
      })
        .then((prepared) => {
          if (!this.isConnected || signal.aborted) return;
          prepared.className = img.className;
          img.replaceWith(prepared);
        })
        .catch(() => {});
    }

    // Title/image clicks carry the shade to the PDP (spec §3d)
    if (variantId) {
      const url = new URL(this.dataset.productUrl, window.location.origin);
      url.searchParams.set('variant', variantId);
      const links = '.tcs-card__link, .tcs-card__title-link, .tcs-card__atc--notify[href]';
      for (const link of this.querySelectorAll(links)) {
        link.href = `${url.pathname}${url.search}`;
      }
      // Sold-out CTA name stays "Notify me — Product — Shade" for the chosen shade
      const notify = this.querySelector('[data-tcs-notify-name]');
      if (notify && swatch.dataset.variantTitle) {
        notify.setAttribute(
          'aria-label',
          `${notify.dataset.tcsNotifyName} — ${swatch.dataset.variantTitle}`,
        );
      }
    }
  }
}

if (!customElements.get('tcs-product-card')) {
  customElements.define('tcs-product-card', TcsProductCard);
}
